import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trophy, Save, ArrowUp, ArrowDown, Star } from 'lucide-react';
import { toast } from 'sonner';
import { getAvatarUrl } from '../utils/avatarUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const levelNames = ['Principiante', 'Base', 'Intermedio', 'Avanzato', 'Esperto'];

const AdminRankingPage = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [category, setCategory] = useState('M');

  useEffect(() => {
    checkAdmin();
  }, []);

  useEffect(() => {
    if (isAdmin) { fetchPlayers(); setHasChanges(false); }
  }, [isAdmin, category]);

  const checkAdmin = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/check`, { withCredentials: true });
      if (!res.data.is_admin) { navigate('/dashboard'); return; }
      setIsAdmin(true);
    } catch { navigate('/dashboard'); }
  };

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      const isUnder20 = category.startsWith('U20');
      const gender = category.includes('F') ? 'F' : 'M';
      let url = `${API_URL}/api/ranking?gender=${gender}`;
      if (isUnder20) url += '&under20=true';
      const res = await axios.get(url, { withCredentials: true });
      setPlayers(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally { setLoading(false); }
  };

  const movePlayer = (index, direction) => {
    const newPlayers = [...players];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newPlayers.length) return;
    [newPlayers[index], newPlayers[targetIndex]] = [newPlayers[targetIndex], newPlayers[index]];
    setPlayers(newPlayers);
    setHasChanges(true);
  };

  const saveRanking = async () => {
    setSaving(true);
    try {
      const isUnder20 = category.startsWith('U20');
      const gender = category.includes('F') ? 'F' : 'M';
      const positions = players.map((p, i) => ({ user_id: p.user_id, position: i + 1 }));
      await axios.put(`${API_URL}/api/admin/ranking`, {
        positions, gender, under20: isUnder20
      }, { withCredentials: true });
      const labels = { M: 'maschile', F: 'femminile', U20M: 'under 20 maschile', U20F: 'under 20 femminile' };
      toast.success(`Classifica ${labels[category]} salvata!`);
      setHasChanges(false);
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const categoryTabs = [
    { key: 'M', label: 'Maschile', color: 'bg-sky-400' },
    { key: 'F', label: 'Femminile', color: 'bg-pink-400' },
    { key: 'U20M', label: 'Under 20 M', color: 'bg-emerald-400' },
    { key: 'U20F', label: 'Under 20 F', color: 'bg-rose-400' },
  ];

  const activeTab = categoryTabs.find(t => t.key === category);

  if (!isAdmin || (loading && players.length === 0)) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-slate-400">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto animate-fadeIn" data-testid="admin-ranking-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-outfit text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" />
            Gestione Classifica
          </h1>
          <p className="text-slate-500 text-sm">Ordina i giocatori con le frecce</p>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white rounded-2xl p-1.5 border border-slate-100 grid grid-cols-2 gap-1.5 mb-6">
        {categoryTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setCategory(tab.key)}
            data-testid={`admin-tab-${tab.key}`}
            className={`py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
              category === tab.key ? `${tab.color} text-white shadow-md` : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="sticky top-2 z-10 mb-4">
          <button
            onClick={saveRanking}
            disabled={saving}
            data-testid="save-ranking-btn"
            className={`w-full py-3.5 text-white rounded-full font-medium transition-all shadow-lg flex items-center justify-center gap-2 ${activeTab?.color || 'bg-amber-500'} hover:opacity-90`}
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <><Save className="w-5 h-5" /> Salva Classifica</>
            )}
          </button>
        </div>
      )}

      {/* Info */}
      <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200 mb-6">
        <p className="text-sm text-amber-700">
          Ordina con le <strong>frecce su/giù</strong>. I Maestri non compaiono in classifica.
          {category.startsWith('U20') && ' Qui appaiono solo giocatori con età inferiore a 20 anni.'}
        </p>
      </div>

      {/* Players List */}
      {players.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 border border-slate-100 text-center">
          <Trophy className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">
            Nessun giocatore trovato
          </p>
          <p className="text-slate-400 text-sm mt-1">
            {category.startsWith('U20') ? "Nessun giocatore under 20 con il profilo aggiornato" : "I giocatori devono aggiornare sesso e profilo"}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {players.map((player, index) => (
            <div key={player.user_id} data-testid={`admin-rank-player-${index}`}
              className="bg-white rounded-2xl p-4 border border-slate-100 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 ${
                index === 0 ? 'bg-amber-400 text-white' : index === 1 ? 'bg-slate-400 text-white' : index === 2 ? 'bg-amber-700 text-white' : 'bg-slate-100 text-slate-600'
              }`}>{index + 1}</div>

              <div className="w-10 h-10 bg-sky-400 rounded-xl flex items-center justify-center text-white font-semibold overflow-hidden flex-shrink-0">
                {getAvatarUrl(player) ? (
                  <img src={getAvatarUrl(player)} alt={player.name} className="w-full h-full rounded-xl object-cover" />
                ) : player.name?.charAt(0)}
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{player.name} {player.surname || ''}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-3 h-3 ${i <= (player.level || 1) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                    ))}
                  </div>
                  <span className="text-xs text-slate-400">{levelNames[(player.level || 1) - 1]}</span>
                  {player.age && <span className="text-xs text-slate-400">· {player.age} anni</span>}
                </div>
              </div>

              <div className="flex flex-col gap-1 flex-shrink-0">
                <button onClick={() => movePlayer(index, -1)} disabled={index === 0} data-testid={`move-up-${index}`}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${index === 0 ? 'text-slate-200' : 'text-slate-500 hover:bg-sky-100 hover:text-sky-600'}`}>
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button onClick={() => movePlayer(index, 1)} disabled={index === players.length - 1} data-testid={`move-down-${index}`}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${index === players.length - 1 ? 'text-slate-200' : 'text-slate-500 hover:bg-sky-100 hover:text-sky-600'}`}>
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminRankingPage;
